import { MouseEvent, ReactNode, useRef, useState } from "react";
import styled from "styled-components";
import {
  FiChevronDown,
  FiChevronUp,
  FiInfo,
  FiCheck,
  FiFileText,
  FiBookmark,
  FiImage,
  FiHome,
  FiMap,
  FiMapPin,
  FiClock,
  FiHash,
  FiUser,
} from "react-icons/fi";
import { ReportDetailTable, ReportChecklist } from "./ReportDetailElements";
import { LuBuilding2, LuRuler, LuEuro, LuShieldCheck } from "react-icons/lu";
import { GoLaw } from "react-icons/go";
import { reportEventBus } from "./ReportEvents";
import { EditablePath, EditableProvider, useEditable } from "../editable/EditableContext";
import { Ed, at } from "./EditableLeaf";
import { AddItem, RemoveItem } from "./EditableList";
import { ToneSelect, SEMAPHORE_OPTIONS } from "./EditableTone";
import { t } from "./i18n";
import { tokenize, type Token } from "./tokenize";
import { CiteMarker, CitationsContext, openCitation, type CitationsMap } from "./Citations";
import { openNorma, normUrl, isNorma } from "./Norms";
import { ReportChart } from "./ReportChart";
import {
  BlockCard,
  BlockHeaderRow,
  BlockNumberPill,
  BlockHeaderMain,
  BlockTitleRow,
  BlockTitle,
  BlockPreview,
  BlockChevron,
  BlockBody,
  RibbonRow,
  RibbonLabel,
  EssenceAccent,
  SynthStrip,
  SynthItem,
  SynthLabelRow,
  SynthDot,
  SynthLabel,
  SynthValue,
  SectionCalloutBox,
  SectionCalloutDot,
  SectionCalloutTitle,
  SectionCalloutText,
  ProseSection,
  ProseLabelCol,
  ProseOverline,
  ProseTitle,
  ProseTagPill,
  ProseBody,
  ProseParagraph,
  SubBlock,
  SubBlockHeading,
  SubBlockNumber,
  FullSubsection,
  FullSubsectionHeader,
  FullSubsectionNumber,
  SubsectionStatus,
  LeadStatusPill,
  LeadStatusIcon,
  CalloutBox,
  CalloutIcon,
  CalloutLabel,
  CalloutTitle,
  CalloutText,
  NoteBox,
  NoteMarker,
  LedgerTitle,
  LedgerBox,
  LedgerRow,
  LedgerLabel,
  LedgerSub,
  LedgerValue,
  MatrixBox,
  MatrixHeaderRow,
  MatrixHeaderCell,
  MatrixHeaderLabel,
  MatrixHeaderTag,
  MatrixRow,
  MatrixRowLabel,
  MatrixCell,
  CitesBox,
  CiteRow,
  CiteNumber,
  CiteRef,
  KeyPointsTable,
  KvTitle,
  KeyPointRow,
  KeyPointCategory,
  KeyPointValue,
  HeroFactsGrid,
  HeroFactCard,
  HeroFactIconBadge,
  HeroFactLabel,
  HeroFactValue,
  HeroFactSub,
  ChipsRow,
  ChipCard,
  ChipLabel,
  ChipValue,
  DocumentCards,
  DocumentCard,
  DocumentCardTitle,
  DocumentCardText,
  EntityCard,
  EntityName,
  EntityMeta,
  SpecsBox,
  SpecRow,
  SpecIcon,
  SpecLabel,
  SpecValue,
  PlainSpecRow,
  PlainSpecLabel,
  PlainSpecValue,
  MutedUpperSpecLabel,
  TimelineSpecRow,
  TimelineSpecLabel,
  SpecsFooter,
  SubLabel,
  SourcesRow,
  SourceChip,
  ObservationsBox,
  ObservationItem,
  AccentTableBox,
  DocTable,
  DocTableHead,
  DocTableRow,
  DocTableCell,
  CriticalityPill,
  FigureGrid,
  FigureCard,
  FigureImage,
  FigureCaption,
  FigureCaptionTitle,
  FigureCaptionText,
  DocumentCardAction,
  ComparablesList,
  ComparableRow,
  ComparablePhoto,
  ComparableBody,
  ComparableHeading,
  ComparableLabel,
  ComparableAddress,
  ComparableRelation,
  ComparableFacts,
  ComparableFact,
  ComparableFactLabel,
  ComparableFactValue,
  ComparableNote,
  ComparableBuilding,
  DocumentCardIcon,
  DocumentCardBody,
  LinksList,
  LinksRow,
  LinksNumber,
  LinksLabel,
  SvgFigure,
  SvgFigureTitle,
  SvgFigureCaption,
} from "./Styles";
import { CartographyImages, isImageFile } from "../components/CartographyImages";
import { EnergyCertificateScale } from "../components/EnergyCertificateScale";
import {
  TINT_NEUTRAL,
  AMBER_INK,
  UNDERLINE_GREEN,
  WHITE,
  TINT_AMBER,
  TINT_RED,
  TINT_RED_PINK,
  TINT_GREEN,
  WASH,
  INK,
  BORDER_SUBTLE,
  SEMAPHORE,
  SEMAPHORE_WITH_NEUTRAL,
  TEXT_VALUE,
  TONE,
  BORDER_DASHED,
} from "../tokens";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Block = any;

/** Editor affordances rendered above each element; see BlockRenderer's `elementControls`. */
export type ElementControls = (index: number, component: string) => ReactNode;

export type FilesMap = Record<string, { publicName: string | null; fileName: string; signedUrl: string }>;

// {{file:ID}} → a SourceChip resolved against `files` (existing behavior).
// {{cite:KEY}} → a clickable superscript that opens the shared citation side
// panel, resolved against `citations` — see CitationSidePanel.tsx. Same
// marker syntax as {{file:...}} so backend-authored text (contentMd,
// findings) can drop either into free text and get both rendered inline.
const REF_RE = /\{\{(file|cite):([^}]+)\}\}/g;
const MARKDOWN_MARKER_RE = /\*\*|\*|_|~|`/;
// "Etiqueta corta: resto de la frase." — see renderObservations below.
const COLON_KV_RE = /^([^:]{2,60}):\s+(.+)$/;
// `[texto](url)` external link syntax — design source's `emphInline`.
const EXTERNAL_LINK_RE = /(\[[^\][]+\]\([^)]+\))/g;
const EXTERNAL_LINK_MATCH_RE = /^\[([^\][]+)\]\(([^)]+)\)$/;
const LEGAL_LINK_STYLE = {
  color: SEMAPHORE.green.text,
  fontWeight: 700,
  textDecoration: "underline",
  textDecorationColor: UNDERLINE_GREEN,
  textUnderlineOffset: "2px",
  cursor: "pointer",
} as const;

// Figures and legal references that get auto-bolded (or, if a recognized
// norm, turned into a clickable link opening NormaModal) even when the
// backend text doesn't wrap them in explicit markdown — ported verbatim
// from the design source's `emphInline` regex.
const LEGAL_FIGURE_RE =
  /(\d[\d.,]*\s?%|\d[\d.,]*\s?€|\d[\d.,]*\s?m²|art(?:s)?\.?\s?\d+[\w.º°/]*|STC\s?\d+\/\d+|STS\s?\d+\/\d+|STSJ\s?[A-Z]{0,3}\s?\d+\/\d+|Orden\s?ECO\/\d+\/\d+|Ley\s?\d+\/\d+|RDL(?:eg)?\s?\d+\/\d+|RD\s?\d+\/\d+|\b(?:LIVA|TRLITPAJD|TRLHL|TRLCI|TRLOTUP|LOTUP|LIS|TRLIRNR|LGT|IIVTNU|AJD|IVA|ITP|IRPF|IRNR|IBI|ICIO|LH|RH|IS|DGSJFP|PGOU|BORME|CENDOJ)\b)/g;

function renderLegalFigures(text: string, key: string): ReactNode {
  if (!text) return text;
  const parts = text.split(LEGAL_FIGURE_RE);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    if (i % 2 === 0) return part || null;
    if (isNorma(part)) {
      return (
        <a
          key={`${key}-n${i}`}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            openNorma(part, normUrl(part));
          }}
          style={LEGAL_LINK_STYLE}
        >
          {part}
        </a>
      );
    }
    return (
      <strong key={`${key}-f${i}`} style={{ fontWeight: 700, color: "#26424b" }}>
        {part}
      </strong>
    );
  });
}

// Same **/*/_/~/` markers the chat's FormattedText renders, so LLM-authored
// content (contentMd, findings) can use markdown emphasis in report text too.
function renderToken(token: Token, key: string): ReactNode {
  switch (token.type) {
    case "bold":
      return <strong key={key}>{token.content}</strong>;
    case "italic":
      return <em key={key}>{token.content}</em>;
    case "strikethrough":
      return (
        <span key={key} style={{ textDecoration: "line-through" }}>
          {token.content}
        </span>
      );
    case "code":
      return (
        <code
          key={key}
          style={{
            background: "rgba(0,0,0,0.06)",
            borderRadius: 3,
            padding: "0.1em 0.3em",
            fontFamily: "monospace",
          }}
        >
          {token.content}
        </code>
      );
    default:
      return renderLegalFigures(token.content, key);
  }
}

function renderPlainOrLegal(text: string, key: string): ReactNode {
  if (!MARKDOWN_MARKER_RE.test(text)) return renderLegalFigures(text, key);
  return tokenize(text).map((token, i) => renderToken(token, `${key}-${i}`));
}

function renderTextSegment(text: string, key: string): ReactNode {
  if (!EXTERNAL_LINK_RE.test(text)) return renderPlainOrLegal(text, key);
  EXTERNAL_LINK_RE.lastIndex = 0;
  return text.split(EXTERNAL_LINK_RE).map((chunk, i) => {
    const match = chunk.match(EXTERNAL_LINK_MATCH_RE);
    if (match) {
      return (
        <a key={`${key}-l${i}`} href={match[2]} target="_blank" rel="noopener noreferrer" style={LEGAL_LINK_STYLE}>
          {match[1]}
        </a>
      );
    }
    return <span key={`${key}-p${i}`}>{renderPlainOrLegal(chunk, `${key}-${i}`)}</span>;
  });
}

export function renderWithFileRefs(
  text: string | undefined,
  files?: FilesMap,
  // Kept for call-site compatibility: {{cite:}} now resolves through
  // CitationsContext, so the map no longer has to be threaded down here.
  _citations?: CitationsMap,
  emphasize = true,
): ReactNode {
  if (!text) return null;
  const renderSegment = emphasize ? renderTextSegment : (value: string) => value;
  // `citations` is resolved by CiteMarker through context, so a text carrying
  // only {{cite:}} markers must go through the parser even without `files`.
  if (!REF_RE.test(text)) return renderSegment(text, "seg");

  REF_RE.lastIndex = 0;
  const parts: ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = REF_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(renderSegment(text.slice(last, match.index), `seg${match.index}`));
    const refType = match[1];
    const key = match[2];

    if (refType === "cite") {
      parts.push(
        <CiteMarker key={key + match.index} cite={key} title={t("dueDiligence.legalFiscalReport.blockRenderer.citeTooltip")} />
      );
    } else {
      const entry = files?.[key];
      if (entry) {
        const label = entry.publicName || entry.fileName;
        parts.push(
          <SourceChip
            key={key + match.index}
            as="span"
            $kind="doc"
            onClick={() => window.open(entry.signedUrl, '_blank')}
            style={{ cursor: "pointer", verticalAlign: "middle" }}
          >
            <FiFileText size={11} />
            {label}
          </SourceChip>
        );
      } else {
        const shortId = key.length > 8 ? key.slice(0, 8) + "…" : key;
        parts.push(
          <SourceChip
            key={key + match.index}
            as="span"
            $kind="doc"
            style={{ verticalAlign: "middle", opacity: 0.55, cursor: "default" }}
            title={t("dueDiligence.legalFiscalReport.blockRenderer.fileNotFound", { docId: key })}
          >
            <FiFileText size={11} />
            {shortId}
          </SourceChip>
        );
      }
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(renderSegment(text.slice(last), `seg${last}`));
  return <>{parts}</>;
}

const PREVIEW_MAX_CHARS = 150;

// Teaser shown under the title while a block is collapsed: the backend-authored
// `preview`, a one-liner used whole. Cutting it by sentence truncated legal
// abbreviations ("del art. 9.1.e LPH · …").
const blockPreview = (block: Block): string | null => {
  if (typeof block.preview !== "string" || !block.preview.trim()) return null;

  // Markers go through the same tokenizer the body uses, so only the ones that
  // actually format are dropped: a bare "~122 m²" keeps its tilde.
  const plain = tokenize(block.preview.replace(REF_RE, ""))
    .map((token) => token.content)
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return null;
  if (plain.length <= PREVIEW_MAX_CHARS) return plain;

  const cut = plain.slice(0, PREVIEW_MAX_CHARS);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
};

const renderLeadStatus = (text?: string) => text ? (
  <LeadStatusPill><LeadStatusIcon><FiCheck size={13} /></LeadStatusIcon>{text}</LeadStatusPill>
) : null;

const renderEnergyRating = (energyRating?: Block["energyRating"]) => {
  if (!energyRating) return null;
  return (
    <EnergyCertificateScale
      consumptionLetter={energyRating.consumption?.letter ?? null}
      consumptionValue={energyRating.consumption?.value ?? null}
      emissionsLetter={energyRating.emissions?.letter ?? null}
      emissionsValue={energyRating.emissions?.value ?? null}
      labels={{
        unavailable: t("dueDiligence.legalFiscalReport.energyScale.unavailable"),
        unavailableCaption: t("dueDiligence.legalFiscalReport.energyScale.unavailableCaption"),
        scaleAlt: t("dueDiligence.legalFiscalReport.energyScale.alt")
      }}
    />
  );
};

// Small named icon set for keyData/heroFacts rows — mirrors the design's own
// icon vocabulary for drawer.cat.specs / drawer.cat.heroFacts. Unknown or
// missing keys fall back to a generic document icon.
const FACT_ICONS: Record<string, ReactNode> = {
  building: <LuBuilding2 size={14} />,
  ruler: <LuRuler size={14} />,
  euro: <LuEuro size={14} />,
  home: <FiHome size={14} />,
  map: <FiMap size={14} />,
  pin: <FiMapPin size={14} />,
  clock: <FiClock size={14} />,
  hash: <FiHash size={14} />,
  document: <FiFileText size={14} />
};
// Keyword → icon fallback for rows that don't set an explicit `icon` (real
// content mostly doesn't yet). Matches the design's own icon choices for
// common cadastral/registry fact labels, so real rows still show varied
// icons instead of all collapsing onto the same generic default.
const LABEL_ICON_PATTERNS: Array<[RegExp, string]> = [
  [/referenc/i, "hash"],
  [/direcci|localizaci/i, "pin"],
  [/clase|calificaci|clasificaci/i, "map"],
  [/uso/i, "document"],
  [/superficie|parcela/i, "ruler"],
  [/edificad|construida|planta/i, "home"],
  [/añ[o0]|construcci[oó]n|fecha/i, "clock"],
  [/valor/i, "euro"]
];
const inferIcon = (label: string): string => LABEL_ICON_PATTERNS.find(([re]) => re.test(label))?.[1] || "document";

const factIcon = (icon?: string, label?: string) => FACT_ICONS[icon || (label && inferIcon(label)) || ""] || <FiFileText size={14} />;

// Per-block "Datos clave" — REAL, populated field. The design's "specs" row
// list (dc.html: drawer.cat.specs): icon + fixed-width label + LEFT-aligned
// value. Distinct from "kv" (right-aligned, no icon) and from "heroFacts"
// (a 3-card grid, see renderHeroFacts) — three visually different patterns.
// No ribbon label above it — the design doesn't show one (same as essence
// losing "Lo esencial").
const renderKeyData = (keyData?: Block["keyData"], footer?: string, files?: FilesMap) => {
  if (!keyData || keyData.length === 0) return null;
  return (
    <>
      <SpecsBox>
        {keyData.map((item: Block["keyData"][number], i: number) => item.variant === "timeline" ? (
          <TimelineSpecRow key={i}>
            <TimelineSpecLabel>{item.label}</TimelineSpecLabel>
            <PlainSpecValue $tone={item.tone}>{renderWithFileRefs(item.value, files)}</PlainSpecValue>
          </TimelineSpecRow>
        ) : item.variant === "mutedUpper" ? (
          <PlainSpecRow key={i}>
            <MutedUpperSpecLabel>{item.label}</MutedUpperSpecLabel>
            <PlainSpecValue $tone={item.tone}>{renderWithFileRefs(item.value, files)}</PlainSpecValue>
          </PlainSpecRow>
        ) : item.variant === "plain" ? (
          <PlainSpecRow key={i}>
            <PlainSpecLabel>{item.label}</PlainSpecLabel>
            <PlainSpecValue $tone={item.tone}>{renderWithFileRefs(item.value, files)}</PlainSpecValue>
          </PlainSpecRow>
        ) : (
          <SpecRow key={i}>
            <SpecIcon>{factIcon(item.icon, item.label)}</SpecIcon>
            <SpecLabel>{item.label}</SpecLabel>
            <SpecValue $tone={item.tone}>{renderWithFileRefs(item.value, files)}</SpecValue>
          </SpecRow>
        ))}
      </SpecsBox>
      {footer && <SpecsFooter>{renderWithFileRefs(footer, files)}</SpecsFooter>}
    </>
  );
};

// The hero-fact 3-card grid (dc.html: drawer.cat.heroFacts) — icon badge +
// label + big value + gray sub-caption. Not populated by any real assembler
// yet (unlike keyData above).
const renderHeroFacts = (heroFacts?: Block["heroFacts"]) => {
  if (!heroFacts || heroFacts.length === 0) return null;
  return (
    <HeroFactsGrid>
      {heroFacts.map((item: Block["heroFacts"][number], i: number) => (
        <HeroFactCard key={i} $tone={item.tone}>
          <HeroFactIconBadge $tone={item.tone}>{factIcon(item.icon, item.label)}</HeroFactIconBadge>
          <div>
            <HeroFactLabel>{item.label}</HeroFactLabel>
            <HeroFactValue $tone={item.tone}>{item.value}</HeroFactValue>
            {item.hint && <HeroFactSub>{item.hint}</HeroFactSub>}
          </div>
        </HeroFactCard>
      ))}
    </HeroFactsGrid>
  );
};

const renderChips = (chips?: Block["chips"], path?: EditablePath) => {
  if (!chips || chips.length === 0) return null;
  return (
    <ChipsRow>
      {chips.map((chip: Block["chips"][number], i: number) => (
        <ChipCard key={i}>
          <ChipLabel><Ed path={at(path, i, "label")} value={chip.label} /></ChipLabel>
          <ChipValue><Ed path={at(path, i, "value")} value={chip.value} /></ChipValue>
          <RemoveItem path={path} index={i} count={chips.length} title="Eliminar chip" />
        </ChipCard>
      ))}
      <AddItem path={path} count={chips.length} label="+ chip" />
    </ChipsRow>
  );
};

const renderDocumentCards = (cards?: Block["documentCards"], files?: FilesMap, path?: EditablePath) => {
  if (!cards || cards.length === 0) return null;
  return <DocumentCards>{cards.map((card: Block["documentCards"][number], i: number) => (
    <DocumentCard key={i}>
      <DocumentCardIcon><FiFileText size={15} /></DocumentCardIcon>
      <DocumentCardBody>
        <DocumentCardTitle><Ed path={at(path, i, "title")} value={card.title} /></DocumentCardTitle>
        <DocumentCardText><Ed path={at(path, i, "text")} value={card.text} multiline render={(v) => renderWithFileRefs(v, files)} /></DocumentCardText>
      </DocumentCardBody>
      {/* A card tied to a citation opens the side panel, same as the
          superscript that cites it from the body text. */}
      {card.cite && (
        <DocumentCardAction type="button" onClick={() => openCitation(card.cite)}>
          {t("dueDiligence.legalFiscalReport.blockRenderer.viewDocument")} →
        </DocumentCardAction>
      )}
      <RemoveItem path={path} index={i} count={cards.length} title="Eliminar ficha" />
    </DocumentCard>
  ))}<AddItem path={path} count={cards.length} label="+ ficha" /></DocumentCards>;
};

// The design's `figs` block. `src` is a {{file:<id>}} token resolved against
// the case documents; an item whose file is missing is dropped rather than
// rendering a broken image.
const FIGURE_SRC_RE = /^\{\{file:([^}]+)\}\}$/;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderFigure = (figure: any, files?: FilesMap, path?: EditablePath) => {
  // Items whose file token does not resolve are dropped, so each surviving one
  // carries the index it had in the payload — that is what an edit must address.
  // The editor keeps them: an image added from the toolbar has no source yet,
  // and dropping it would leave nothing on screen to type the source into.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items = (figure?.items || []).flatMap((item: any, sourceIndex: number) => {
    const token = String(item.src || "").match(FIGURE_SRC_RE);
    const src = token ? files?.[token[1]]?.signedUrl : item.src;
    return src || path ? [{ ...item, src, sourceIndex }] : [];
  });
  if (items.length === 0) return null;
  const columns = items.length > 1 ? Math.min(items.length, Number(figure.columns) || items.length) : 1;
  return (
    <FigureGrid $columns={columns}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {items.map((item: any, i: number) => (
        <FigureCard key={i}>
          {item.src
            ? <FigureImage src={item.src} alt={item.label || ""} />
            : <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
                height: 90, borderRadius: 8, background: WASH, color: INK.ghost }}>
                <FiImage size={20} />
              </div>}
          {/* The source is a file token ({{file:ID}}, copied from the editor's
              attachment panel) or a plain URL. It has no place in the read-only
              design, so it only shows while editing. */}
          {path && (
            <span style={{ display: "block", fontSize: 11, color: INK.quiet, marginTop: 4 }}>
              <Ed
                path={at(path, "items", item.sourceIndex, "src")}
                value={String(figure.items[item.sourceIndex]?.src ?? "")}
                placeholder="{{file:ID}} o https://…"
              />
            </span>
          )}
          {(item.label || item.caption || path) && (
            <FigureCaption>
              {(item.label || path) && <FigureCaptionTitle><Ed path={at(path, "items", item.sourceIndex, "label")} value={item.label} placeholder="Título" /></FigureCaptionTitle>}
              {(item.caption || path) && <FigureCaptionText><Ed path={at(path, "items", item.sourceIndex, "caption")} value={item.caption} multiline placeholder="Pie de imagen" /></FigureCaptionText>}
            </FigureCaption>
          )}
          <RemoveItem path={at(path, "items")} index={item.sourceIndex} count={figure.items.length} title="Eliminar imagen" />
        </FigureCard>
      ))}
    </FigureGrid>
  );
};

// The design's `comps` block: one row per sale with its façade photo on the
// left and the building and note under the figures. `photo` resolves like a
// figure's `src`; a row whose photo is missing keeps an empty frame.
const renderComparables = (items?: Block["items"], files?: FilesMap, citations?: CitationsMap, path?: EditablePath) => {
  if (!items || items.length === 0) return null;
  const label = (key: string) => t(`dueDiligence.legalFiscalReport.blockRenderer.comparables.${key}`);
  return (
    <ComparablesList>
      {items.map((item: Block["items"][number], i: number) => {
        const token = String(item.photo || "").match(FIGURE_SRC_RE);
        const photo = token ? files?.[token[1]]?.signedUrl : item.photo;
        const facts = [["area", item.area], ["price", item.price], ["unitPrice", item.unitPrice], ["date", item.date]] as const;
        return (
          <ComparableRow key={i} $highlight={!!item.highlight}>
            {photo
              ? <ComparablePhoto href={photo} target="_blank" rel="noopener noreferrer"><img src={photo} alt={item.address || ""} /></ComparablePhoto>
              : <ComparablePhoto as="span" />}
            <ComparableBody>
              <ComparableHeading>
                <ComparableLabel><Ed path={at(path, i, "label")} value={item.label} placeholder="COMP" /></ComparableLabel>
                <ComparableAddress><Ed path={at(path, i, "address")} value={item.address} placeholder="Dirección" /></ComparableAddress>
                {(item.relation || path) && <ComparableRelation $highlight={!!item.highlight}><Ed path={at(path, i, "relation")} value={item.relation} placeholder="Relación" /></ComparableRelation>}
              </ComparableHeading>
              <ComparableFacts>
                {facts.map(([key, value]) => (
                  <ComparableFact key={key}>
                    <ComparableFactLabel>{label(key)}</ComparableFactLabel>
                    <ComparableFactValue $strong={key === "unitPrice"}><Ed path={at(path, i, key)} value={value} placeholder="—" /></ComparableFactValue>
                  </ComparableFact>
                ))}
              </ComparableFacts>
              {(item.building || item.note || path) && (
                <ComparableNote>
                  {(item.building || path) && <ComparableBuilding><Ed path={at(path, i, "building")} value={item.building} placeholder="Edificio" /></ComparableBuilding>}
                  {item.building && item.note && " · "}
                  <Ed path={at(path, i, "note")} value={item.note} multiline placeholder="Nota" render={(v) => renderWithFileRefs(v, files, citations)} />
                </ComparableNote>
              )}
              {path && (
                <span style={{ display: "block", fontSize: 11, color: INK.quiet, marginTop: 4 }}>
                  <Ed path={at(path, i, "photo")} value={String(item.photo ?? "")} placeholder="{{file:ID}} o https://…" />
                </span>
              )}
            </ComparableBody>
            <RemoveItem path={path} index={i} count={items.length} title="Eliminar comparable" />
          </ComparableRow>
        );
      })}
      <AddItem path={path} count={items.length} label="+ comparable" />
    </ComparablesList>
  );
};

// `tone: "amber"` is the same card painted the way Benalmádena's titular is.
const renderEntityCard = (entity?: Block["entityCard"], files?: FilesMap) => entity ? (
  <EntityCard $tone={entity.tone}>
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.9rem" }}>
      <span style={{ width: 40, height: 40, borderRadius: 10, background: entity.tone === TONE.amber ? SEMAPHORE.amber.text : SEMAPHORE.green.fill, color: WHITE, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><FiUser size={20} /></span>
      <div><EntityName>{entity.name}</EntityName><EntityMeta $tone={entity.tone}>CIF {entity.cif}</EntityMeta></div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.55rem 1.1rem" }}>
      {entity.details?.map((item: Block["entityCard"]["details"][number], i: number, all: unknown[]) => (
        // An odd last detail takes the whole row (Torrent's third); an even
        // count stays two by two.
        <div key={i} style={{ gridColumn: i === all.length - 1 && all.length % 2 === 1 ? "1 / -1" : undefined }}>
          <PlainSpecLabel>{item.label}</PlainSpecLabel>
          <div style={{ marginTop: 3 }}><PlainSpecValue $tone={item.tone}>{renderWithFileRefs(item.value, files)}</PlainSpecValue></div>
        </div>
      ))}
    </div>
  </EntityCard>
) : null;

// Section-level "synth strip" (MIKE-322 follow-up): a handful of headline
// figures shown once per section, right below the section header — NOT per
// block. Rendered from FinalReport/index.tsx, not from BlockRenderer's own
// per-block flow. admin-interface passes the strip's `path` to edit it.
type SynthCard = { label: string; value: string; tone?: string };

// The design's strip has four cards. While editing, the slots a section is
// missing show as dashed guides that are not in the payload: saving without
// touching them stores nothing, and typing into one or picking its colour
// appends that card alone.
const SYNTH_GUIDE_SLOTS = 4;

const SynthCards = ({ synth, path }: { synth?: SynthCard[]; path?: EditablePath }) => {
  const { editable, onChange } = useEditable();
  const cards = synth ?? [];
  const editing = !!path && editable;
  const guides = editing ? Math.max(0, SYNTH_GUIDE_SLOTS - cards.length) : 0;
  if (cards.length === 0 && guides === 0) return null;
  const fillGuide = (field: EditablePath, value: unknown) =>
    onChange(path!, [...cards, { label: "", value: "", tone: "neutral", [field[0]]: value }]);
  return (
    <>
      {/* While editing, the strip's bottom margin moves below "+ tarjeta", so the
          button sits against the cards it adds to and apart from the summary. */}
      <SynthStrip style={editing ? { marginBottom: cards.length < SYNTH_GUIDE_SLOTS ? undefined : 0 } : undefined}>
        {cards.map((item, i) => (
          <SynthItem key={i} $first={i === 0}>
            <SynthLabelRow>
              <SynthDot $tone={item.tone || "neutral"} />
              <SynthLabel><Ed path={at(path, i, "label")} value={item.label} placeholder="Etiqueta" /></SynthLabel>
              <span style={{ marginLeft: "auto" }}><RemoveItem path={path} index={i} count={cards.length} title="Eliminar tarjeta" /></span>
            </SynthLabelRow>
            <SynthValue $tone={item.tone || "neutral"}><Ed path={at(path, i, "value")} value={item.value} placeholder="Valor" /></SynthValue>
            <ToneSelect path={at(path, i, "tone")} value={item.tone || "neutral"} options={SEMAPHORE_OPTIONS} />
          </SynthItem>
        ))}
        {Array.from({ length: guides }, (_, g) => (
          <SynthItem key={`guide-${g}`} $first={cards.length + g === 0} style={{ opacity: 0.55, outline: `1px dashed ${BORDER_DASHED}`, outlineOffset: -4, borderRadius: 6 }}>
            <EditableProvider onChange={fillGuide}>
              <SynthLabelRow>
                <SynthDot $tone="neutral" />
                <SynthLabel><Ed path={["label"]} value="" placeholder="Etiqueta" /></SynthLabel>
              </SynthLabelRow>
              <SynthValue $tone="neutral"><Ed path={["value"]} value="" placeholder="Valor" /></SynthValue>
              <ToneSelect path={["tone"]} value="neutral" options={SEMAPHORE_OPTIONS} />
            </EditableProvider>
          </SynthItem>
        ))}
      </SynthStrip>
      {editing && cards.length >= SYNTH_GUIDE_SLOTS && (
        <div style={{ margin: "0.3rem 0 1.375rem" }}>
          <AddItem path={path} count={cards.length} label="+ tarjeta" />
        </div>
      )}
    </>
  );
};

export const renderSynthStrip = (synth?: SynthCard[], path?: EditablePath) =>
  <SynthCards synth={synth} path={path} />;

// One highlighted takeaway box per section (MIKE-322), right after the synth
// strip, before its blocks — see SectionCalloutBox in Styles.ts. Rendered
// from FinalReport/index.tsx, same call pattern as renderSynthStrip.
export const renderSectionCallouts = (callouts?: Array<{ title: string; text: string; tone?: string }>) => {
  if (!callouts || callouts.length === 0) return null;
  return (
    <>
      {callouts.map((c, i) => (
        <SectionCalloutBox key={i} $tone={c.tone}>
          <SectionCalloutDot $tone={c.tone} />
          <div>
            <SectionCalloutTitle>{c.title}</SectionCalloutTitle>
            <SectionCalloutText>{c.text}</SectionCalloutText>
          </div>
        </SectionCalloutBox>
      ))}
    </>
  );
};

// Splits prose on sentence boundaries, then greedily regroups into ≤200-char
// paragraphs — same chunking as the design source so long-form text reads in
// short paragraphs without the assembler having to pre-format it.
const SENTENCE_BOUNDARY_RE = /(?<=[.:])\s+(?=[A-ZÁÉÍÓÚÜÑ«¿¡])/;
const PROSE_CHUNK_MAX_CHARS = 200;

export function chunkProse(text: string): string[] {
  const sentences = text.split(SENTENCE_BOUNDARY_RE);
  const chunks: string[] = [];
  let current = "";
  for (const sentence of sentences) {
    if (!current) {
      current = sentence;
    } else if ((current + " " + sentence).length <= PROSE_CHUNK_MAX_CHARS) {
      current += " " + sentence;
    } else {
      chunks.push(current);
      current = sentence;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

const renderCallout = (callout: Block["callout"], files?: FilesMap, citations?: CitationsMap, path?: EditablePath) => {
  if (!callout) return null;
  const kind = callout.kind === "conclusion" ? "conclusion" : "applied";
  // The report may name its own eyebrow (a callout about the transaction, not
  // the property); otherwise it comes from the kind.
  const label =
    callout.label ||
    (kind === "conclusion"
      ? t("dueDiligence.legalFiscalReport.blockRenderer.calloutConclusionLabel")
      : t("dueDiligence.legalFiscalReport.blockRenderer.calloutAppliedLabel"));
  return (
    <CalloutBox $kind={kind}>
      <CalloutIcon>
        {callout.icon === "shield" ? <LuShieldCheck size={18} /> : <FiCheck size={17} strokeWidth={2.5} />}
      </CalloutIcon>
      <div>
        <CalloutLabel>{label}</CalloutLabel>
        <CalloutTitle><Ed path={at(path, "title")} value={callout.title} /></CalloutTitle>
        {/* Same treatment as prose: the text is chunked by sentence and the
            first chunk leads in bold, which is how the design paints a
            callout body. Editing works on the unchunked text. */}
        <Ed
          path={at(path, "text")}
          value={callout.text}
          multiline
          render={(v) => chunkProse(v).map((chunk, i) => (
            <CalloutText key={i} $lead={i === 0}>
              {renderWithFileRefs(chunk, files, citations, !callout.plainText)}
            </CalloutText>
          ))}
        />
      </div>
    </CalloutBox>
  );
};

// A callout with a `tone` (favorable / reservas / critico / neutral) is the
// design's dot-and-title pill, the same one a section's own `callouts` paint
// — placed here, inside a block, it lands wherever the elements put it
// instead of above every block.
const renderToneCallout = (callout: { title?: string; text?: string; tone: string }, path?: EditablePath) => (
  <SectionCalloutBox $tone={callout.tone}>
    <SectionCalloutDot $tone={callout.tone} />
    <div>
      <SectionCalloutTitle><Ed path={at(path, "title")} value={callout.title} /></SectionCalloutTitle>
      <SectionCalloutText><Ed path={at(path, "text")} value={callout.text} multiline /></SectionCalloutText>
    </div>
  </SectionCalloutBox>
);

const CALLOUT_STYLE_OPTIONS = [
  { value: "applied", label: "Aplicado" },
  { value: "conclusion", label: "Conclusión" },
  { value: "favorable", label: "Favorable" },
  { value: "reservas", label: "Reservas" },
  { value: "critico", label: "Crítico" },
  { value: "neutral", label: "Neutro" },
];

const withCalloutStyle = (callout: Block, picked: string) =>
  picked === "applied" || picked === "conclusion"
    ? { ...callout, kind: picked, tone: undefined }
    : { ...callout, tone: picked };

const renderKvRows = (rows: Array<{ label: string; value: string; tone?: string }>, files?: FilesMap, path?: EditablePath) => (
  <>
  <KeyPointsTable>
    <tbody>
      {rows.map((row, i) => (
        <KeyPointRow key={i}>
          <KeyPointCategory><Ed path={at(path, i, "label")} value={row.label} /></KeyPointCategory>
          {/* A toned value is one colour end to end, as the design paints it:
              auto-emphasis would recolour its figures and norms on their own. */}
          <KeyPointValue $tone={row.tone}><Ed path={at(path, i, "value")} value={row.value} multiline render={(v) => renderWithFileRefs(v, files, undefined, !row.tone || row.tone === "neutral")} /></KeyPointValue>
          {path && <td style={{ width: 1, textAlign: "right", whiteSpace: "nowrap" }}>
            <ToneSelect path={at(path, i, "tone")} value={row.tone || "neutral"} options={SEMAPHORE_OPTIONS} />
            <RemoveItem path={path} index={i} count={rows.length} />
          </td>}
        </KeyPointRow>
      ))}
    </tbody>
  </KeyPointsTable>
  <AddItem path={path} count={rows.length} />
  </>
);

const renderKv = (kv: Block["kv"], files?: FilesMap, path?: EditablePath) => {
  if (!kv || kv.length === 0) return null;
  return renderKvRows(kv, files, path);
};

// The ledger paints no red; the design variant adds a muted grey.
const LEDGER_TONE_OPTIONS = SEMAPHORE_OPTIONS.filter((o) => o.value !== "red");
const LEDGER_DESIGN_TONE_OPTIONS = [...LEDGER_TONE_OPTIONS, { value: "muted", label: "Gris" }];

const renderLedger = (ledger: Block["ledger"], path?: EditablePath) => {
  if (!ledger || !ledger.items || ledger.items.length === 0) return null;
  return (
    <>
      {(ledger.title || path) && <LedgerTitle><Ed path={at(path, "title")} value={ledger.title} placeholder="Título" /></LedgerTitle>}
      <LedgerBox>
        {ledger.items.map((item: Block["ledger"]["items"][number], i: number) => (
          <LedgerRow key={i}>
            <span>
              <LedgerLabel><Ed path={at(path, "items", i, "label")} value={item.label} /></LedgerLabel>
              {(item.sub || path) && <LedgerSub><Ed path={at(path, "items", i, "sub")} value={item.sub} placeholder="Detalle" /></LedgerSub>}
            </span>
            <LedgerValue $tone={item.tone} style={ledger.variant === "design" ? { color: item.tone === TONE.green ? SEMAPHORE.green.text : item.tone === "muted" ? INK.label : INK.strong } : undefined}><Ed path={at(path, "items", i, "value")} value={item.value} /></LedgerValue>
            <ToneSelect path={at(path, "items", i, "tone")} value={item.tone || "neutral"} options={ledger.variant === "design" ? LEDGER_DESIGN_TONE_OPTIONS : LEDGER_TONE_OPTIONS} />
            <RemoveItem path={at(path, "items")} index={i} count={ledger.items.length} title="Eliminar línea" />
          </LedgerRow>
        ))}
        <AddItem path={at(path, "items")} count={ledger.items.length} label="+ línea" />
      </LedgerBox>
    </>
  );
};

const renderMatrix = (matrix: Block["matrix"], path?: EditablePath) => {
  if (!matrix || !matrix.columns || matrix.columns.length === 0) return null;
  const cols = matrix.columns.length;
  const comparison = matrix.variant === "comparison";
  const highlightColumn = matrix.highlightColumn ?? (cols === 2 ? 0 : -1);
  const rowStyle = comparison ? { minWidth: cols === 2 ? 510 : 620 } : undefined;
  return (
    <MatrixBox style={comparison ? { overflowX: "auto" } : undefined}>
      <MatrixHeaderRow $cols={cols} style={rowStyle}>
        <div>
          <ToneSelect path={at(path, "highlightColumn")} title="Columna resaltada"
            value={String(highlightColumn)}
            options={[{ value: "-1", label: "Sin resaltar" }, ...matrix.columns.map((col: { label: string }, i: number) => ({ value: String(i), label: col.label || `Columna ${i + 1}` }))]}
            toValue={Number} />
        </div>
        {matrix.columns.map((col: Block["matrix"]["columns"][number], i: number) => (
          <MatrixHeaderCell key={i} $highlight={i === highlightColumn}>
            <MatrixHeaderLabel style={comparison ? { fontSize: cols === 2 ? 12.5 : 11.5, color: cols === 2 && i !== highlightColumn ? INK.quiet : TEXT_VALUE } : undefined}><Ed path={at(path, "columns", i, "label")} value={col.label} /></MatrixHeaderLabel>
            {(col.tag || path) && <MatrixHeaderTag style={comparison && i !== highlightColumn ? { color: INK.label, background: TINT_NEUTRAL } : undefined}><Ed path={at(path, "columns", i, "tag")} value={col.tag} placeholder="Etiqueta" /></MatrixHeaderTag>}
          </MatrixHeaderCell>
        ))}
      </MatrixHeaderRow>
      {matrix.rows.map((row: Block["matrix"]["rows"][number], ri: number) => (
        <MatrixRow key={ri} $cols={cols} style={rowStyle}>
          <MatrixRowLabel><Ed path={at(path, "rows", ri, "label")} value={row.label} /></MatrixRowLabel>
          {row.cells.map((cell: string, ci: number) => (
            <MatrixCell key={ci} $highlight={ci === highlightColumn} style={comparison ? { fontSize: cols === 2 ? 12 : 11.5, color: cols === 3 || ci === highlightColumn ? INK.strong : INK.label } : undefined}>
              <Ed path={at(path, "rows", ri, "cells", ci)} value={cell} multiline />
            </MatrixCell>
          ))}
          {path && <RemoveItem path={at(path, "rows")} index={ri} count={matrix.rows.length} />}
        </MatrixRow>
      ))}
      <AddItem path={at(path, "rows")} count={matrix.rows.length} />
    </MatrixBox>
  );
};

const renderCites = (cites: Block["cites"], files?: FilesMap, path?: EditablePath) => {
  if (!cites || cites.length === 0) return null;
  return (
    <CitesBox>
      {cites.map((cite: string, i: number) => (
        <CiteRow key={i}>
          <CiteNumber>{i + 1}</CiteNumber>
          <CiteRef><Ed path={at(path, i)} value={cite} render={(v) => renderWithFileRefs(v, files)} /></CiteRef>
          <RemoveItem path={path} index={i} count={cites.length} title="Eliminar cita" />
        </CiteRow>
      ))}
      <AddItem path={path} count={cites.length} label="+ cita" />
    </CitesBox>
  );
};

// Each source renders as its own pill. The icon + visual style classifies
// the citation:
//   - law (Art./Arts./Artículo, Ley, RD/RDL, D-L/DL, DA/DF, BOE/BOJA/BOP,
//     STC/STS, Resolución, TRL…) → balance-of-law icon, link to BOE.
//   - section ("Sección N") → bookmark icon + green styling, link to that
//     section of this very report. If the source carries no explicit href,
//     we auto-generate "#section-N" from the label.
//   - everything else (apartado X, Informe Y, "Nota Simple", "IBI", entity
//     names, …) → document icon, neutral grey styling.
const LAW_PREFIX_RE =
  /^\s*(Art(?:\.|s|s\.|í|ículo|ículos)?\s|Ley\b|RD\b|RDL\b|D-?L\b|DA\b|DF\b|BOE\b|BOJA\b|BOP\b|STC\b|STS\b|Res\.|Resolución\b|TRL|V\d|Memento\b)/i;

// Captures the leading section number (e.g. "Sección 7", "Sección 7.3" → "7").
// We only target the top-level section: sub-block scrolling isn't wired yet.
const SECTION_NUMBER_RE = /^\s*Secci(?:ó|o)n\s+(\d+)/i;

type SourceKind = "law" | "section" | "doc" | "image";

const sourceKind = (label: string): SourceKind => {
  if (LAW_PREFIX_RE.test(label)) return "law";
  if (SECTION_NUMBER_RE.test(label)) return "section";
  return "doc";
};

const sourceIcon = (kind: SourceKind) => {
  if (kind === "law") return <GoLaw size={11} />;
  if (kind === "section") return <FiBookmark size={11} />;
  if (kind === "image") return <FiImage size={11} />;
  return <FiFileText size={11} />;
};

// Image sources style like a doc chip (grey); only their icon differs. So the
// SourceChip styling never needs an "image" variant.
const chipKind = (kind: SourceKind): "law" | "section" | "doc" => kind === "image" ? "doc" : kind;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SourceLike = string | { label?: string; name?: string; href?: string };

const sourceLabel = (s: SourceLike): string =>
  typeof s === "string" ? s : s.label || s.name || "";

const sourceHref = (s: SourceLike): string | undefined =>
  typeof s === "string" ? undefined : s.href;

const resolveSourceHref = (label: string, explicitHref?: string): string | undefined => {
  if (explicitHref) return explicitHref;
  // Fallback: auto-link "Sección N" labels to the local section anchor.
  const m = SECTION_NUMBER_RE.exec(label);
  return m ? `#section-${m[1]}` : undefined;
};

const isExternalHref = (href: string) => /^https?:\/\//i.test(href);

const FILE_TOKEN_RE = /^\{\{file:([^}]+)\}\}$/;

export const renderSources = (sources?: SourceLike[], files?: FilesMap) => {
  if (!sources || sources.length === 0) return null;

  type Entry = {
    label: string;
    href?: string;
    kind: SourceKind;
    id?: string;
    onClick?: () => void;
    broken?: boolean;
  };

  const INLINE_FILE_RE = /\{\{file:([^}]+)\}\}/g;

  const entries: Entry[] = sources
    .flatMap((s): (Entry | null)[] => {
      const raw = typeof s === "string" ? s : s.label || s.name || "";

      const fileMatch = FILE_TOKEN_RE.exec(raw);
      if (fileMatch) {
        const docId = fileMatch[1];
        const fileEntry = files?.[docId];
        if (fileEntry) {
          return [{ label: fileEntry.publicName || fileEntry.fileName, href: fileEntry.signedUrl, kind: isImageFile(fileEntry.fileName) ? "image" : "doc", id: docId }];
        }
        const shortId = docId.length > 8 ? docId.slice(0, 8) + "…" : docId;
        return [{ label: shortId, kind: "doc", broken: true, id: docId }];
      }

      // Handle space-separated tokens in a single string, e.g. "{{file:a}} {{file:b}}"
      INLINE_FILE_RE.lastIndex = 0;
      if (INLINE_FILE_RE.test(raw)) {
        INLINE_FILE_RE.lastIndex = 0;
        const results: Entry[] = [];
        let m: RegExpExecArray | null;
        while ((m = INLINE_FILE_RE.exec(raw)) !== null) {
          const docId = m[1];
          const fileEntry = files?.[docId];
          if (fileEntry) {
            results.push({ label: fileEntry.publicName || fileEntry.fileName, href: fileEntry.signedUrl, kind: isImageFile(fileEntry.fileName) ? "image" : "doc" });
          } else {
            const shortId = docId.length > 8 ? docId.slice(0, 8) + "…" : docId;
            results.push({ label: shortId, kind: "doc", broken: true });
          }
        }
        if (results.length > 0) return results;
      }

      const label = sourceLabel(s);
      if (!label) return [null];
      const explicitHref = sourceHref(s);
      const href = resolveSourceHref(label, explicitHref);
      const kind: SourceKind = explicitHref && isExternalHref(explicitHref) ? "law" : sourceKind(label);
      return [{ label, href, kind }];
    })
    .filter((e): e is Entry => e !== null && !!e.label);

  if (entries.length === 0) return null;

  // Image file sources (e.g. §3 cartography) ALSO render inline via the shared
  // CartographyImages component; the "Fuentes" list below stays intact.
  const imageEntries = entries.filter((e) => e.kind === "image" && !!e.href && !e.broken);
  const pillEntries = entries;

  return (
    <>
      <CartographyImages images={imageEntries} />
      {pillEntries.length > 0 && (
        <>
          <SubLabel>
            <FiFileText size={11} style={{ verticalAlign: "-1px", marginRight: "0.3rem" }} />
            {t("dueDiligence.legalFiscalReport.blockRenderer.sourcesLabel")}
          </SubLabel>
          <SourcesRow>
            {pillEntries.map((e, i) => {
          if (e.broken) {
            return (
              <SourceChip key={i} $kind="doc" style={{ opacity: 0.5, cursor: "default" }} title={t("dueDiligence.legalFiscalReport.blockRenderer.fileNotFoundShort")}>
                {sourceIcon("doc")}
                {e.label}
              </SourceChip>
            );
          }
          if (!e.href) {
            return (
              <SourceChip key={i} $kind={chipKind(e.kind)}>
                {sourceIcon(e.kind)}
                {e.label}
              </SourceChip>
            );
          }
          const isInternalSection = /^#section-\d+$/.test(e.href);
          if (isInternalSection) {
            return (
              <SourceChip
                key={i}
                $kind={chipKind(e.kind)}
                as="a"
                href={e.href}
                onClick={(ev: MouseEvent<HTMLAnchorElement>) => {
                  ev.preventDefault();
                  reportEventBus.emit("REPORT_SCROLL_TO_SECTION", e.href!.slice(1));
                }}
              >
                {sourceIcon(e.kind)}
                {e.label}
              </SourceChip>
            );
          }
          return (
            <SourceChip
              key={i}
              $kind={chipKind(e.kind)}
              as="a"
              href={e.href}
              {...(isExternalHref(e.href)
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
            >
              {sourceIcon(e.kind)}
              {e.label}
            </SourceChip>
          );
            })}
          </SourcesRow>
        </>
      )}
    </>
  );
};

export const renderObservations = (observations?: string[], files?: FilesMap) => {
  if (!observations || observations.length === 0) return null;

  // When every observation is shaped "Etiqueta: resto de la frase." (the
  // backend already writes many this way — e.g. section13Environment.ts's
  // "Calidad del aire: Buena.", "Zona acústica: ..."), it reads as the
  // design's `kv` label/value box, not a bullet list — reshape it as such
  // without touching the backend. A mixed array (some items with no colon,
  // e.g. free-prose recommendations) falls back to the plain bullet list.
  const kvRows = observations.map((o) => o.match(COLON_KV_RE)).every((m) => m)
    ? observations.map((o) => {
        const m = o.match(COLON_KV_RE)!;
        return { label: m[1], value: m[2] };
      })
    : null;
  if (kvRows) return renderKvRows(kvRows, files);

  return (
    <>
      <RibbonRow>
        <RibbonLabel>
          <FiInfo size={12} /> {t("dueDiligence.legalFiscalReport.blockRenderer.observationsLabel")}
        </RibbonLabel>
      </RibbonRow>
      <ObservationsBox>
        {observations.map((o, i) => (
          <ObservationItem key={i}>{renderWithFileRefs(o, files)}</ObservationItem>
        ))}
      </ObservationsBox>
    </>
  );
};

// Same shape used by the urbanistic report (DocTableData): headers + rows of
// cells. Lifted into BlockRenderer so legal-fiscal blocks can render tables
// without forking the renderer. Cells are plain strings (no badge variant
// support yet — add when a legal-fiscal block needs it).
interface DocTableData {
  headers: string[];
  rows: Array<{ cells: string[]; actionSubtitle?: string; statusTone?: string }>;
  headersPlain?: boolean;
  boldColumns?: number[];
  hideHeader?: boolean;
  compact?: boolean;
  showLabel?: boolean;
  variant?: "actions";
}

const DocTableSubtext = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 10.5px;
  font-weight: 400;
  color: ${INK.faint};
`;

// Labels resolve at render, not at module load: the host app registers its
// translator at startup and the language can change afterwards.
const criticalityLabel = (key: string) =>
  t(`dueDiligence.legalFiscalReport.blockRenderer.criticality.${key}`);

const CRITICIDAD_PILL: Record<string, { bg: string; color: string; label: () => string }> = {
  alta: { bg: TINT_RED, color: "#b0470f", label: () => criticalityLabel("alta") },
  media: { bg: TINT_AMBER, color: AMBER_INK, label: () => criticalityLabel("media") },
  baja: { bg: TINT_GREEN[200], color: "#4a7a5e", label: () => criticalityLabel("baja") },
  "en la firma": { bg: TINT_AMBER, color: SEMAPHORE.amber.text, label: () => criticalityLabel("enLaFirma") },
  bloqueante: { bg: TINT_RED_PINK, color: SEMAPHORE.red.text, label: () => criticalityLabel("bloqueante") },
  cumplido: { bg: TINT_GREEN[200], color: SEMAPHORE.green.text, label: () => criticalityLabel("cumplido") },
  "post-firma": { bg: TINT_GREEN[200], color: SEMAPHORE.green.text, label: () => criticalityLabel("postFirma") },
  "parcial 02/09": { bg: TINT_AMBER, color: SEMAPHORE.amber.text, label: () => "Parcial 02/09" },
  orientativo: { bg: TINT_NEUTRAL, color: SEMAPHORE_WITH_NEUTRAL.neutral.text, label: () => criticalityLabel("orientativo") },
};

const renderDocTable = (table?: DocTableData, path?: EditablePath) => {
  if (!table || !table.rows || table.rows.length === 0) return null;
  const critIdx = table.headers.findIndex(
    (h) => h.toLowerCase() === "criticidad"
  );
  return (
    <>
      {table.showLabel !== false && <RibbonRow>
        <RibbonLabel>
          <FiFileText size={12} /> {t("dueDiligence.legalFiscalReport.blockRenderer.documentationLabel")}
        </RibbonLabel>
      </RibbonRow>}
      <AccentTableBox>
        <DocTable $compact={!!table.compact}>
          {!table.hideHeader && <DocTableHead>
            <tr>
              {path && <th style={{ width: 1 }} />}
              {table.headers.map((h, i) => (
                <th
                  key={i}
                  style={
                    table.headersPlain
                      ? { textTransform: "none", letterSpacing: "normal", fontSize: "0.875rem" }
                      : i === critIdx
                      ? { textAlign: "center" }
                      : undefined
                  }
                >
                  <Ed path={at(path, "headers", i)} value={h} />
                </th>
              ))}
            </tr>
          </DocTableHead>}
          <tbody>
            {table.rows.map((row, i) => (
              <DocTableRow key={i}>
                {row.cells.map((cell, j) => {
                  if (j === critIdx) {
                    const tone = CRITICIDAD_PILL[(row.statusTone || cell).toLowerCase()];
                    return (
                      <DocTableCell key={j} style={{ textAlign: "center" }}>
                        <CriticalityPill $bg={tone?.bg ?? TINT_NEUTRAL} $color={tone?.color ?? INK.muted}>
                          <Ed path={at(path, "rows", i, "cells", j)} value={cell} render={() => tone?.label() ?? cell} />
                        </CriticalityPill>
                        <ToneSelect path={at(path, "rows", i, "statusTone")} title="Criticidad"
                          value={row.statusTone || ""}
                          options={[{ value: "", label: "Según texto" }, ...Object.entries(CRITICIDAD_PILL).map(([value, pill]) => ({ value, label: pill.label() }))]}
                          toValue={(picked) => picked || undefined} />
                      </DocTableCell>
                    );
                  }
                  if (isExternalHref(cell)) {
                    return (
                      <DocTableCell key={j}>
                        <a href={cell} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                          <SourceChip $kind="law">
                            <GoLaw size={10} />
                            {t("dueDiligence.legalFiscalReport.blockRenderer.viewTableLink")}
                          </SourceChip>
                        </a>
                      </DocTableCell>
                    );
                  }
                  return (
                    <DocTableCell key={j} style={table.boldColumns?.includes(j) ? { fontWeight: 700, color: TEXT_VALUE } : undefined}>
                      <Ed path={at(path, "rows", i, "cells", j)} value={cell} multiline />
                      {table.variant === "actions" && j === 1 && (row.actionSubtitle || path) && <DocTableSubtext><Ed path={at(path, "rows", i, "actionSubtitle")} value={row.actionSubtitle} placeholder="Detalle" /></DocTableSubtext>}
                    </DocTableCell>
                  );
                })}
                {path && <DocTableCell style={{ width: 1, textAlign: "right" }}>
                  <RemoveItem path={at(path, "rows")} index={i} count={table.rows.length} />
                </DocTableCell>}
              </DocTableRow>
            ))}
          </tbody>
        </DocTable>
      </AccentTableBox>
      <AddItem path={at(path, "rows")} count={table.rows.length} />
    </>
  );
};

// The design's `links` block: numbered official sources, each an outbound
// link. Items without a url render as plain numbered text (e.g. doctrine
// resolutions with no public deep link).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderLinks = (items?: any[], path?: EditablePath) => {
  if (!items?.length) return null;
  return (
    <LinksList>
      {items.map((item, i) => (
        <LinksRow key={item.number ?? i}>
          <LinksNumber>{item.number ?? String(i + 1).padStart(2, "0")}</LinksNumber>
          <LinksLabel>
            {item.href && !path
              ? <a href={item.href} target="_blank" rel="noopener noreferrer"><Ed path={at(path, i, "label")} value={item.label} /></a>
              : <Ed path={at(path, i, "label")} value={item.label} />}
            {/* The destination has no place in the read-only design — the label
                carries the link — so the editor gets its own line for it. */}
            {path && (
              <span style={{ display: "block", fontSize: 11, color: INK.quiet }}>
                <Ed path={at(path, i, "href")} value={item.href} placeholder="https://…" />
              </span>
            )}
          </LinksLabel>
          <RemoveItem path={path} index={i} count={items.length} title="Eliminar enlace" />
        </LinksRow>
      ))}
      <AddItem path={path} count={items.length} label="+ enlace" />
    </LinksList>
  );
};

// A drawing pasted whole, as SVG markup — the report's escape hatch for a
// figure nobody has written a chart type for yet.
//
// It goes into an <img> rather than into the DOM: markup from outside can carry
// <script>, on* handlers and foreignObject, and a browser runs none of that
// inside an <img>. The cost is that the drawing does not inherit the report's
// fonts or colours, so it has to be self-contained — which is what a pasted SVG
// is anyway.
const svgDataUri = (source?: string): string | null => {
  const markup = (source ?? "").trim();
  if (!markup.startsWith("<svg")) return null;
  return `data:image/svg+xml;utf8,${encodeURIComponent(markup)}`;
};

const renderSvgElement = (element: Block, path?: EditablePath) => {
  const src = svgDataUri(element.source);
  return (
    <SvgFigure>
      {(element.title || path) && (
        <SvgFigureTitle><Ed path={at(path, "title")} value={element.title} placeholder="Título del gráfico" /></SvgFigureTitle>
      )}
      {src
        ? <img src={src} alt={element.title || ""} style={{ width: "100%", height: "auto", display: "block" }} />
        : path && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center",
            height: 90, borderRadius: 8, background: WASH, color: INK.ghost }}>
            <FiImage size={20} />
          </div>
        )}
      {/* The markup itself: read-only reports never show it, the editor shows it
          under the drawing so it can be pasted over. */}
      {path && (
        <div style={{ fontSize: 10.5, color: INK.quiet, marginTop: 6, wordBreak: "break-all" }}>
          <Ed path={at(path, "source")} value={element.source} multiline placeholder="Pega aquí el SVG (<svg …>…</svg>)" />
        </div>
      )}
      {(element.caption || path) && (
        <SvgFigureCaption><Ed path={at(path, "caption")} value={element.caption} multiline placeholder="Pie del gráfico" /></SvgFigureCaption>
      )}
    </SvgFigure>
  );
};

// The element vocabulary a report may use in `elements`. Keep it in sync with
// the switch below: an unknown `component` renders nothing, silently, so the
// contract test walks the reference report against this list.
export const ELEMENT_COMPONENTS = [
  "paragraph", "accent", "prose", "ledger", "matrix", "chart", "docTable",
  "detailTable", "chips", "checklist", "callout", "note", "cites",
  "figure", "svg", "kv", "documentCards", "links", "energyRating",
  "heroFacts", "entityCard", "keyData", "leadStatus", "comparables",
] as const;

// Ordered elements let the report choose component placement without section-specific rules.
const renderElements = (
  elements: Block[],
  files?: FilesMap,
  citations?: CitationsMap,
  path?: EditablePath,
  controls?: ElementControls,
) => elements.map((element, index) => {
  const key = element.id || index;
  const p = at(path, index);
  const painted = ((): ReactNode => {
  switch (element.component) {
    case "paragraph": return <ProseParagraph key={key} $lead={false}><Ed path={at(p, "text")} value={element.text} multiline render={(v) => element.emphasize ? renderWithFileRefs(v, files, citations) : v} /></ProseParagraph>;
    case "accent": return <EssenceAccent key={key}><Ed path={at(p, "text")} value={element.text} multiline render={(v) => element.plainText ? v : renderWithFileRefs(v, files, citations)} /></EssenceAccent>;
    // A subsection headed across the full width — number, title and status
    // pill on one line, the text below — as the legacy `prose` paints it.
    case "prose": if (element.layout === "full") return (
      <FullSubsection key={key}>
        <FullSubsectionHeader>
          <ProseTitle>
            {(element.number || p) && <FullSubsectionNumber><Ed path={at(p, "number")} value={element.number} placeholder="Nº" /></FullSubsectionNumber>}
            <Ed path={at(p, "title")} value={element.title} placeholder="Título" />
          </ProseTitle>
          {/* Like the side-label layout: while editing, an empty badge still
              shows, so a subsection without one can be given one. */}
          {(element.badge || p) && <span><SubsectionStatus $tone={element.badge?.tone}><Ed path={at(p, "badge", "label")} value={element.badge?.label} placeholder="Etiqueta" /></SubsectionStatus><ToneSelect path={at(p, "badge", "tone")} value={element.badge?.tone || "green"} options={SEMAPHORE_OPTIONS} /></span>}
        </FullSubsectionHeader>
        {element.paragraphs.map((paragraph: string, pi: number) => (
          <Ed key={pi} path={at(p, "paragraphs", pi)} value={paragraph} multiline block
            render={(v) => chunkProse(v).map((chunk, ci) => <ProseParagraph key={ci} $lead={false}>{renderWithFileRefs(chunk, files, citations)}</ProseParagraph>)} />
        ))}
      </FullSubsection>
    );
    return (
      <ProseSection key={key} style={{ borderTop: `1px solid ${BORDER_SUBTLE}`, paddingTop: 15, marginTop: 16 }}>
        <ProseLabelCol>
          {(element.number || p) && <ProseOverline><Ed path={at(p, "number")} value={element.number} placeholder="Nº" /></ProseOverline>}
          {(element.title || p) && <ProseTitle><Ed path={at(p, "title")} value={element.title} placeholder="Título" /></ProseTitle>}
          {(element.badge || p) && <ProseTagPill $tone={element.badge?.tone}><Ed path={at(p, "badge", "label")} value={element.badge?.label} placeholder="Etiqueta" /></ProseTagPill>}
          <ToneSelect path={at(p, "badge", "tone")} value={element.badge?.tone || "neutral"} options={SEMAPHORE_OPTIONS} />
        </ProseLabelCol>
        <ProseBody>
          {element.paragraphs.map((paragraph: string, pi: number) => (
            /* The editable unit is the whole paragraph; chunkProse only decides
               how it is painted, so editing a chunk would silently drop the rest. */
            <div key={pi} style={{ marginBottom: 15 }}>
              <Ed
                path={at(p, "paragraphs", pi)}
                value={paragraph}
                multiline
                block
                render={(v) => chunkProse(v).map((chunk, ci) => <ProseParagraph key={ci} $lead={ci === 0}>{renderWithFileRefs(chunk, files, citations)}</ProseParagraph>)}
              />
              <RemoveItem path={at(p, "paragraphs")} index={pi} count={element.paragraphs.length} title="Eliminar párrafo" />
            </div>
          ))}
          <AddItem path={at(p, "paragraphs")} count={element.paragraphs.length} label="+ párrafo" />
        </ProseBody>
      </ProseSection>
    );
    case "ledger": return <div key={key}>{renderLedger(element, p)}</div>;
    case "matrix": return <div key={key}>{renderMatrix(element, p)}</div>;
    case "chart": return <ReportChart key={key} chart={element} path={p} />;
    case "docTable": return <div key={key}>{renderDocTable(element, p)}</div>;
    case "cites": return <div key={key}>{renderCites(element.items, files, at(p, "items"))}</div>;
    case "note": return <NoteBox key={key}><NoteMarker>◆</NoteMarker><span><Ed path={at(p, "text")} value={element.text} multiline render={(v) => renderWithFileRefs(v, files, citations)} /></span></NoteBox>;
    case "chips": return <div key={key}>{renderChips(element.items, at(p, "items"))}</div>;
    case "detailTable": return <ReportDetailTable key={key} rows={element.rows} variant={element.variant} path={at(p, "rows")} />;
    case "checklist": return <ReportChecklist key={key} items={element.items} path={at(p, "items")} renderText={(text) => renderWithFileRefs(text, files, citations, false)} />;
    case "callout": return (
      <div key={key}>
        <ToneSelect path={p} title="Estilo" options={CALLOUT_STYLE_OPTIONS}
          value={element.tone || (element.kind === "conclusion" ? "conclusion" : "applied")}
          toValue={(picked) => withCalloutStyle(element, picked)} />
        {element.tone ? renderToneCallout(element, p) : renderCallout(element, files, citations, p)}
      </div>
    );
    case "figure": return <div key={key}>{renderFigure(element, files, p)}</div>;
    case "energyRating": return <div key={key}>{renderEnergyRating(element)}</div>;
    // The three icon cards and the titular card the legacy blocks already paint.
    case "heroFacts": return <div key={key}>{renderHeroFacts(element.items)}</div>;
    case "entityCard": return <div key={key}>{renderEntityCard(element, files)}</div>;
    case "keyData": return <div key={key}>{renderKeyData(element.rows, element.footer, files)}</div>;
    case "leadStatus": return <div key={key}>{renderLeadStatus(element.text)}</div>;
    case "svg": return <div key={key}>{renderSvgElement(element, p)}</div>;
    case "kv": return (
      <div key={key}>
        {(element.title || p) && <KvTitle><Ed path={at(p, "title")} value={element.title} placeholder="Título" /></KvTitle>}
        {renderKv(element.rows, files, at(p, "rows"))}
      </div>
    );
    case "documentCards": return <div key={key}>{renderDocumentCards(element.items, files, at(p, "items"))}</div>;
    case "links": return <div key={key}>{renderLinks(element.items, at(p, "items"))}</div>;
    case "comparables": return <div key={key}>{renderComparables(element.items, files, citations, at(p, "items"))}</div>;
    default: return null;
  }
  })();
  // admin-interface hangs its per-element toolbar (drag, delete) above each one.
  // The client report passes no controls and gets the element on its own.
  return controls ? <div key={key}>{controls(index, element.component)}{painted}</div> : painted;
});

const renderSubblocks = (subblocks?: Block["subblocks"], files?: FilesMap, citations?: CitationsMap, path?: EditablePath) => {
  if (!subblocks || subblocks.length === 0) return null;
  return subblocks.map((sub: Block["subblocks"][number], i: number) => (
    <SubBlock key={sub.number || i}>
      <SubBlockHeading><SubBlockNumber>{sub.number}</SubBlockNumber><Ed path={at(path, i, "title")} value={sub.title} /></SubBlockHeading>
      {renderElements(sub.elements || [], files, citations, at(path, i, "elements"))}
    </SubBlock>
  ));
};

interface Props {
  block: Block;
  defaultOpen?: boolean;
  files?: FilesMap;
  citations?: CitationsMap;
  // Renders the editor's toolbar above each element of an `elements` block.
  elementControls?: ElementControls;
  // Where this block sits in the report payload, e.g. ["sections", 2, "blocks", 0].
  // admin-interface's editor passes it and wraps the tree in an EditableProvider,
  // which turns every text leaf below into a click-to-edit field. user-interface
  // leaves it undefined and the block renders read-only, exactly as before.
  blockPath?: EditablePath;
}

export const BlockRenderer = ({ block, defaultOpen = false, files, citations, blockPath, elementControls }: Props) => {
  const blockRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(defaultOpen);
  const preview = blockPreview(block);

  return (
    <CitationsContext.Provider value={citations || {}}>
    <BlockCard ref={blockRef}>
      <BlockHeaderRow onClick={() => setOpen(!open)}>
        <BlockHeaderMain>
          <BlockTitleRow>
            {(block.numberLabel ?? block.number) && <BlockNumberPill style={block.numberLabel !== undefined ? { width: "auto", height: "auto", padding: "3px 8px", borderRadius: 6 } : undefined}>{block.numberLabel ?? block.number}</BlockNumberPill>}
            <BlockTitle><Ed path={at(blockPath, "title")} value={block.title} /></BlockTitle>
          </BlockTitleRow>
          {!open && preview && <BlockPreview>{preview}</BlockPreview>}
        </BlockHeaderMain>
        <BlockChevron>{open ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}</BlockChevron>
      </BlockHeaderRow>

      <BlockBody $open={open}>
        {open && (
          <>
            {renderElements(block.elements || [], files, citations, at(blockPath, "elements"), elementControls)}
            {renderSubblocks(block.subblocks, files, citations, at(blockPath, "subblocks"))}
          </>
        )}
      </BlockBody>
    </BlockCard>
    </CitationsContext.Provider>
  );
};
